import React from "react";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: any;
  iconPosition?: "left" | "right";
  isLoading?: boolean;
  isFullWidth?: boolean;
}

const combineClasses = (...classes: (string | undefined)[]) => {
  return classes.filter(Boolean).join(" ");
};

// Animation variants
const buttonVariants = {
  initial: {
    scale: 1,
    opacity: 1,
  },
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: "easeInOut",
    },
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: 0.1,
      ease: "easeInOut",
    },
  },
  disabled: {
    opacity: 0.6,
    scale: 1,
    transition: {
      duration: 0.2,
    },
  },
};

const iconVariants = {
  initial: {
    opacity: 0,
    x: -10,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
    x: 10,
    transition: {
      duration: 0.2,
    },
  },
};

const loadingSpinnerVariants = {
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: "linear",
    },
  },
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      icon,
      iconPosition = "left",
      isLoading = false,
      isFullWidth = false,
      className = "",
      disabled,
      ...props
    },
    ref,
  ) => {
    const LoadingSpinner = () => (
      <motion.svg
        variants={loadingSpinnerVariants}
        animate="animate"
        className="h-4 w-4 mr-2"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </motion.svg>
    );

    const variantClasses: Record<ButtonVariant, string> = {
      primary:
        "bg-bnp-cyan text-white hover:bg-opacity-90 border border-transparent",
      secondary:
        "bg-gray-100 text-gray-800 hover:bg-gray-200 border border-transparent",
      outline:
        "bg-transparent text-gray-700 border border-gray-300 hover:bg-gray-50",
      ghost:
        "bg-transparent text-gray-600 hover:bg-gray-100 border border-transparent",
      danger:
        "bg-red-600 text-white hover:bg-red-700 border border-transparent",
    };

    const sizeClasses: Record<ButtonSize, string> = {
      sm: "px-3 py-1.5 text-xs rounded-md",
      md: "px-4 py-2 text-sm rounded-lg",
      lg: "px-6 py-3 text-base rounded-lg",
    };

    const IconWrapper = ({ children }: { children: React.ReactNode }) => (
      <motion.span
        variants={iconVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className={combineClasses(
          "inline-flex items-center justify-center",
          children ? (iconPosition === "left" ? "mr-2" : "ml-2") : "",
          "w-5 h-5",
        )}
      >
        {children}
      </motion.span>
    );

    return (
      //@ts-ignore
      <motion.button
        ref={ref}
        variants={buttonVariants}
        initial="initial"
        whileHover={disabled || isLoading ? "disabled" : "hover"}
        whileTap={disabled || isLoading ? "disabled" : "tap"}
        animate={disabled || isLoading ? "disabled" : "initial"}
        className={twMerge(
          "flex justify-center items-center gap-2 font-medium transition-colors",
          variantClasses[variant],
          sizeClasses[size],
          isFullWidth ? "w-full" : "",
          disabled || isLoading
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer",
          className,
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : icon && iconPosition === "left" ? (
          <IconWrapper>{icon}</IconWrapper>
        ) : null}

        <motion.span
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.span>

        {icon && iconPosition === "right" && !isLoading ? (
          <IconWrapper>{icon}</IconWrapper>
        ) : null}
      </motion.button>
    );
  },
);

Button.displayName = "Button";

export default Button;
